import type { ProjectedRadialPoint, ScreenPoint } from './radialLayout';
import type { RadialHover } from './renderRadial';
import type { RadialPlot } from './types';

/** The pointer, and the plot it was recorded against. */
export type PointerState = {
  plot: RadialPlot;
  hover: RadialHover | null;
  at: ScreenPoint;
  source: 'pointer' | 'keyboard';
};

/** The pointer, unless it belongs to a plot that has since been replaced. */
export const toCurrentPointer = (
  pointer: PointerState | null,
  plot: RadialPlot
): PointerState | null => (pointer?.plot === plot ? pointer : null);

/** Keeps the previous hover's identity while the same vertex stays under the cursor. */
export const toNextHover = (
  previous: RadialHover | null,
  hit: ProjectedRadialPoint | null
): RadialHover | null => {
  if (!hit) return null;
  if (
    previous?.seriesIndex === hit.seriesIndex &&
    previous.pointIndex === hit.pointIndex
  ) {
    return previous;
  }
  return { seriesIndex: hit.seriesIndex, pointIndex: hit.pointIndex };
};

export type NextPointerArgs = {
  previous: PointerState | null;
  plot: RadialPlot;
  at: ScreenPoint;
  hit: ProjectedRadialPoint | null;
};

export const toNextPointer = ({
  previous,
  plot,
  at,
  hit,
}: NextPointerArgs): PointerState => ({
  plot,
  at,
  source: 'pointer',
  hover: toNextHover(toCurrentPointer(previous, plot)?.hover ?? null, hit),
});

export type KeyboardPointerArgs = {
  plot: RadialPlot;
  hover: RadialHover | null;
  projected: ProjectedRadialPoint[];
};

/** A keyboard-driven pointer, anchored on the vertex it names, or `null` once cleared. */
export const toKeyboardPointer = ({
  plot,
  hover,
  projected,
}: KeyboardPointerArgs): PointerState | null => {
  if (!hover) return null;
  const vertex = projected.find(
    ({ seriesIndex, pointIndex }) =>
      seriesIndex === hover.seriesIndex && pointIndex === hover.pointIndex
  );
  return {
    plot,
    hover,
    at: vertex ? { x: vertex.x, y: vertex.y } : { x: 0, y: 0 },
    source: 'keyboard',
  };
};
